/**
 * Inventario page — replaces the legacy "add product" flow.
 *
 * Shows a table of stock products with publish toggle, quantity, price, barcode
 * and a kebab menu for Edit / Edit qty / Delete actions.
 *
 * Floating Action Button (➕) at bottom-right opens the existing ProductForm
 * (type=product). "Importa inventario" / "Esporta inventario" buttons handle
 * Excel/CSV import/export via the new /api/inventory/* endpoints.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Search, FileSpreadsheet, Download, Trash2,
  Hash, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

import Header from '../components/Header';
import BottomNav from '../components/BottomNav';
import BackButton from '../components/BackButton';
import ProductForm from '../components/shop/ProductForm';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtPrice = (p) => (p == null ? '—' : `€ ${Number(p).toFixed(2).replace('.', ',')}`);

export default function InventarioPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [qtyEditing, setQtyEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [importOpen, setImportOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      // v1.8.3 — /inventory now paginates (default 50). Fetch the max page
      // size (500) until we introduce server-side pagination UI.
      const res = await axios.get(`${API}/inventory?limit=500`, { withCredentials: true });
      setItems(res.data || []);
    } catch (err) {
      if (err.response?.status === 404) navigate('/registrazione-attivita');
      else toast.error('Errore nel caricamento inventario');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await axios.get(`${API}/inventory?limit=500`, { withCredentials: true });
        if (alive) setItems(res.data || []);
      } catch (err) {
        if (alive && err.response?.status === 404) navigate('/registrazione-attivita');
        else if (alive) toast.error('Errore nel caricamento inventario');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [navigate]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((p) =>
      (p.nome || '').toLowerCase().includes(q)
      || (p.barcode || '').toLowerCase().includes(q)
      || (p.sku || '').toLowerCase().includes(q),
    );
  }, [items, search]);

  const handlePublishToggle = async (item) => {
    const newVal = !(item.pubblicato !== false);
    try {
      await axios.post(`${API}/inventory/${item.id}/publish`, { pubblicato: newVal }, { withCredentials: true });
      setItems((prev) => prev.map((p) => (p.id === item.id ? { ...p, pubblicato: newVal } : p)));
      toast.success(newVal ? 'Prodotto pubblicato su Kyloo' : 'Rimosso dalla vetrina');
    } catch {
      toast.error('Errore nel salvataggio');
    }
  };

  const handleDelete = async (item) => {
    try {
      await axios.delete(`${API}/products/${item.id}`, { withCredentials: true });
      setItems((prev) => prev.filter((p) => p.id !== item.id));
      toast.success('Prodotto eliminato');
    } catch {
      toast.error("Errore durante l'eliminazione");
    } finally {
      setConfirmDelete(null);
    }
  };

  const handleExport = (fmt) => {
    // Use the cookie session via fetch->blob to trigger browser download with auth.
    fetch(`${API}/inventory/export?fmt=${fmt}`, { credentials: 'include' })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `inventario.${fmt}`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      });
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-24" data-testid="inventario-page">
      <Header />

      <main className="max-w-6xl mx-auto px-4 py-4">
        <BackButton fallback="/dashboard" className="mb-3" />
        {/* Header section */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-4">
          <div>
            <h1 className="font-outfit font-bold text-2xl sm:text-3xl text-slate-800">I tuoi prodotti</h1>
            <p className="text-sm text-slate-500">Gestisci, controlla e pubblica i prodotti del tuo negozio.</p>
          </div>
          <div className="relative sm:w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cerca prodotto, barcode, SKU…"
              className="pl-9 rounded-xl bg-white"
              data-testid="inventory-search-input"
            />
          </div>
        </div>

        {/* Action tiles: Import + Export */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
          <button
            type="button"
            onClick={() => setImportOpen(true)}
            className="flex items-center gap-3 p-4 bg-white border-2 border-emerald-200 hover:border-emerald-400 rounded-xl text-left transition-colors"
            data-testid="import-tile"
          >
            <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
              <FileSpreadsheet className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <div className="font-semibold text-slate-800">Importa inventario</div>
              <div className="text-xs text-slate-500">Carica file Excel / CSV</div>
            </div>
          </button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                className="flex items-center gap-3 p-4 bg-white border-2 border-blue-200 hover:border-blue-400 rounded-xl text-left transition-colors"
                data-testid="export-tile"
              >
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                  <Download className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <div className="font-semibold text-slate-800">Esporta inventario</div>
                  <div className="text-xs text-slate-500">Scarica come XLSX o CSV</div>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={() => handleExport('xlsx')} data-testid="export-xlsx">Excel (.xlsx)</DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleExport('csv')} data-testid="export-csv">CSV (.csv)</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          <div className="hidden sm:grid grid-cols-12 gap-3 px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-slate-500 border-b border-slate-100">
            <div className="col-span-2">Stato</div>
            <div className="col-span-3">Prodotto</div>
            <div className="col-span-2">Categoria</div>
            <div className="col-span-1 text-right">Prezzo</div>
            <div className="col-span-2 text-right">Quantità</div>
            <div className="col-span-2 text-right">Azioni</div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-6 h-6 text-blue-600 animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 px-4">
              <p className="text-slate-500">Nessun prodotto. Clicca su <strong>+</strong> per aggiungerne uno o importa un file Excel.</p>
            </div>
          ) : (
            <ul>
              {filtered.map((item) => (
                <InventoryRow
                  key={item.id}
                  item={item}
                  onPublishToggle={handlePublishToggle}
                  onEdit={() => { setEditingProduct(item); setShowForm(true); }}
                  onEditQty={() => setQtyEditing(item)}
                  onDelete={() => setConfirmDelete(item)}
                />
              ))}
            </ul>
          )}
        </div>

        <p className="text-xs text-slate-500 mt-3" data-testid="inventory-total">Totale prodotti: {items.length}</p>
      </main>

      {/* Floating Action Button (➕) */}
      <button
        type="button"
        onClick={() => { setEditingProduct(null); setShowForm(true); }}
        className="fixed bottom-24 right-5 w-14 h-14 rounded-full bg-blue-600 hover:bg-blue-700 text-white shadow-lg flex items-center justify-center transition-transform hover:scale-105 z-40"
        aria-label="Aggiungi prodotto"
        data-testid="add-product-fab"
      >
        <Plus className="w-7 h-7" />
      </button>

      <BottomNav />

      {/* New / Edit product form */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto p-0">
          <ProductForm
            product={editingProduct}
            tipo="product"
            onCancel={() => setShowForm(false)}
            onSave={() => { setShowForm(false); load(); }}
          />
        </DialogContent>
      </Dialog>

      {/* Edit quantity modal */}
      {qtyEditing && (
        <EditQtyModal
          item={qtyEditing}
          onClose={() => setQtyEditing(null)}
          onSaved={(nextQty) => {
            setItems((prev) => prev.map((p) => (p.id === qtyEditing.id ? { ...p, quantita_magazzino: nextQty } : p)));
            setQtyEditing(null);
          }}
        />
      )}

      {/* Delete confirmation */}
      <Dialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Elimina prodotto</DialogTitle>
            <DialogDescription>
              Sei sicuro di voler eliminare <strong>{confirmDelete?.nome}</strong>? L&apos;operazione non è reversibile.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDelete(null)} data-testid="cancel-delete">Annulla</Button>
            <Button variant="destructive" onClick={() => handleDelete(confirmDelete)} data-testid="confirm-delete">Elimina</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ImportWizard open={importOpen} onClose={() => setImportOpen(false)} onDone={() => { setImportOpen(false); load(); }} />
    </div>
  );
}

// ───────────────────────────────────────────── ROW ─────────────────────────────────────────────

function InventoryRow({ item, onPublishToggle, onEdit, onEditQty, onDelete }) {
  const published = item.pubblicato !== false;
  // The row itself is clickable → opens the edit dialog. Inline action buttons
  // stop propagation so they don't trigger the row click.
  const stop = (e) => e.stopPropagation();
  return (
    <li
      role="button"
      tabIndex={0}
      onClick={onEdit}
      onKeyDown={(e) => { if (e.key === 'Enter') onEdit(); }}
      className="grid grid-cols-12 gap-3 items-center px-4 py-3 border-b border-slate-50 hover:bg-slate-50 cursor-pointer"
      data-testid={`inventory-row-${item.id}`}
    >
      <div className="col-span-2 sm:col-span-2 flex items-center gap-2" onClick={stop} onKeyDown={stop}>
        {published ? (
          <>
            <span className="text-red-600 font-semibold text-xs uppercase tracking-wide" data-testid={`status-published-${item.id}`}>
              Pubblicato
            </span>
            <Button
              size="sm"
              variant="outline"
              className="text-[11px] h-7 border-red-300 text-red-700 hover:bg-red-50"
              onClick={(e) => { e.stopPropagation(); onPublishToggle(item); }}
              data-testid={`unpublish-btn-${item.id}`}
            >
              Rimuovi dalla vetrina
            </Button>
          </>
        ) : (
          <Button
            size="sm"
            variant="outline"
            className="text-xs h-7 border-blue-300 text-blue-700 hover:bg-blue-50"
            onClick={(e) => { e.stopPropagation(); onPublishToggle(item); }}
            data-testid={`publish-btn-${item.id}`}
          >
            Pubblica
          </Button>
        )}
      </div>

      <div className="col-span-10 sm:col-span-3 flex items-center gap-3">
        {item.immagini?.[0] ? (
          <img src={item.immagini[0]} alt="" className="w-10 h-10 rounded-lg object-cover bg-slate-100" />
        ) : (
          <div className="w-10 h-10 rounded-lg bg-slate-100" />
        )}
        <div className="min-w-0">
          <div className="font-medium text-slate-800 truncate">{item.nome}</div>
          {(item.barcode || item.sku) && (
            <div className="text-xs text-slate-400 flex items-center gap-1" data-testid={`barcode-${item.id}`}>
              <Hash className="w-3 h-3" />
              {item.barcode || item.sku}
            </div>
          )}
        </div>
      </div>

      <div className="col-span-6 sm:col-span-2 text-sm text-slate-600">{item.categoria || '—'}</div>
      <div className="col-span-3 sm:col-span-1 text-right text-sm font-medium text-slate-700">{fmtPrice(item.prezzo)}</div>
      <div className="col-span-2 sm:col-span-2 text-right" onClick={stop} onKeyDown={stop}>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onEditQty(); }}
          className="inline-flex items-center justify-center min-w-[44px] px-2 py-1 rounded-lg bg-slate-100 hover:bg-slate-200 text-sm font-semibold text-slate-800"
          data-testid={`qty-btn-${item.id}`}
        >
          {item.quantita_magazzino ?? 0}
        </button>
      </div>

      <div className="col-span-1 text-right" onClick={stop} onKeyDown={stop}>
        <Button
          size="sm"
          variant="outline"
          className="h-8 border-red-300 text-red-600 hover:bg-red-50 hover:border-red-500"
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          data-testid={`delete-btn-${item.id}`}
        >
          <Trash2 className="w-4 h-4" />
          <span className="hidden sm:inline ml-1">Elimina</span>
        </Button>
      </div>
    </li>
  );
}

// ──────────────────────────────────────── QTY EDIT MODAL ──────────────────────────────────────

function EditQtyModal({ item, onClose, onSaved }) {
  const current = item.quantita_magazzino ?? 0;
  const [next, setNext] = useState(String(current));
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);

  const parsed = Math.max(0, parseInt(next, 10) || 0);
  const delta = parsed - current;

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/inventory/${item.id}/qty`, { new_qty: parsed }, { withCredentials: true });
      toast.success(`Quantità aggiornata: ${current} → ${parsed}`);
      onSaved(parsed);
    } catch {
      toast.error("Errore nell'aggiornamento quantità");
    } finally {
      setSaving(false);
      setConfirming(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Modifica quantità — {item.nome}</DialogTitle>
        </DialogHeader>

        {!confirming ? (
          <>
            <div className="space-y-3">
              <div className="text-sm text-slate-500">Quantità attuale: <strong className="text-slate-800">{current}</strong></div>
              <div>
                <Label htmlFor="new_qty">Nuova quantità</Label>
                <Input
                  id="new_qty"
                  type="number"
                  min={0}
                  value={next}
                  onChange={(e) => setNext(e.target.value)}
                  className="mt-1 rounded-xl"
                  autoFocus
                  data-testid="qty-new-input"
                />
              </div>
              {parsed !== current && (
                <div className={`text-sm font-medium ${delta < 0 ? 'text-red-600' : 'text-emerald-600'}`} data-testid="qty-variation">
                  Variazione: {delta > 0 ? '+' : ''}{delta} unità
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Annulla</Button>
              <Button
                disabled={parsed === current}
                onClick={() => setConfirming(true)}
                className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="qty-continue"
              >
                Continua
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-900" data-testid="qty-confirm-text">
              Sei sicuro di voler modificare la quantità di <strong>{item.nome}</strong> da <strong>{current}</strong> a <strong>{parsed}</strong> pezzi?
              <div className="mt-1 font-semibold">Variazione: {delta > 0 ? '+' : ''}{delta} unità</div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirming(false)} disabled={saving}>Annulla</Button>
              <Button onClick={handleSave} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="qty-confirm">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Conferma'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

// ──────────────────────────────────────── IMPORT WIZARD ────────────────────────────────────────

function ImportWizard({ open, onClose, onDone }) {
  const [phase, setPhase] = useState('upload'); // upload | map | confirm | done
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null); // {headers, rows, suggested_mapping}
  const [mapping, setMapping] = useState({});
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const resetAndClose = () => {
    setPhase('upload');
    setFile(null);
    setPreview(null);
    setMapping({});
    setResult(null);
    onClose();
  };

  const fields = ['barcode', 'sku', 'nome', 'categoria', 'descrizione', 'prezzo', 'quantita_magazzino'];

  const handleUpload = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/inventory/import/preview`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPreview(res.data);
      setMapping(res.data.suggested_mapping || {});
      setPhase('map');
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore lettura file");
    } finally {
      setBusy(false);
    }
  };

  const buildRows = () => preview.rows.map((row) => {
    const obj = {};
    for (const f of fields) {
      const idx = mapping[f];
      if (idx === undefined || idx === null || idx === '') continue;
      const v = row[Number(idx)];
      if (f === 'prezzo') obj[f] = v === '' ? null : Number(String(v).replace(',', '.')) || null;
      else if (f === 'quantita_magazzino') obj[f] = parseInt(v, 10) || 0;
      else obj[f] = String(v ?? '').trim();
    }
    return obj;
  });

  const handleConfirm = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/inventory/import/confirm`, { rows: buildRows() }, { withCredentials: true });
      setResult(res.data);
      setPhase('done');
      toast.success(`Importazione completata: ${res.data.created} nuovi, ${res.data.updated} aggiornati`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore importazione');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && resetAndClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Importa inventario</DialogTitle>
          <DialogDescription>
            {phase === 'upload' && 'Carica un file Excel (.xlsx, .xls) o CSV con i tuoi prodotti.'}
            {phase === 'map' && 'Verifica e correggi le associazioni colonne → campi Kyloo.'}
            {phase === 'confirm' && 'Riepilogo righe da importare.'}
            {phase === 'done' && 'Importazione completata.'}
          </DialogDescription>
        </DialogHeader>

        {phase === 'upload' && (
          <div className="space-y-4">
            <Input type="file" accept=".xlsx,.xls,.csv" onChange={(e) => setFile(e.target.files?.[0])} data-testid="import-file-input" />
            {file && <div className="text-sm text-slate-600">File: {file.name}</div>}
            <DialogFooter>
              <Button variant="outline" onClick={resetAndClose}>Annulla</Button>
              <Button onClick={handleUpload} disabled={!file || busy} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="import-analyze">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Analizza file'}
              </Button>
            </DialogFooter>
          </div>
        )}

        {phase === 'map' && preview && (
          <div className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {fields.map((f) => (
                <div key={f}>
                  <Label className="text-xs uppercase text-slate-500">{f}</Label>
                  <select
                    className="w-full mt-1 rounded-xl border border-slate-200 px-3 py-2 bg-white text-sm"
                    value={mapping[f] ?? ''}
                    onChange={(e) => setMapping((m) => ({ ...m, [f]: e.target.value === '' ? undefined : Number(e.target.value) }))}
                    data-testid={`mapping-${f}`}
                  >
                    <option value="">— Non mappato —</option>
                    {preview.headers.map((h, i) => (
                      <option key={`col-${i}-${h || 'empty'}`} value={i}>Colonna {String.fromCharCode(65 + i)}: {h || `(vuota)`}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>
            <div className="text-xs text-slate-500">Anteprima prime righe: {preview.rows.length}</div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPhase('upload')}>Indietro</Button>
              <Button onClick={() => setPhase('confirm')} disabled={mapping.nome === undefined} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="import-next">
                Avanti
              </Button>
            </DialogFooter>
          </div>
        )}

        {phase === 'confirm' && preview && (
          <div className="space-y-3">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-sm text-amber-900">
              Stai per importare <strong>{preview.rows.length}</strong> righe.
              Per ogni riga: se barcode o SKU esistono → aggiorno; altrimenti creo un nuovo prodotto.
              Nessun prodotto verrà eliminato automaticamente.
            </div>
            <div className="overflow-x-auto rounded-xl border border-slate-100">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                  <tr>{fields.map((f) => <th key={f} className="px-2 py-2 text-left">{f}</th>)}</tr>
                </thead>
                <tbody>
                  {buildRows().slice(0, 10).map((r, i) => (
                    <tr key={`preview-${r.barcode || r.sku || r.nome || i}-${i}`} className="border-t border-slate-100">
                      {fields.map((f) => <td key={f} className="px-2 py-2">{String(r[f] ?? '')}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setPhase('map')} disabled={busy}>Indietro</Button>
              <Button onClick={handleConfirm} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="import-confirm">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Conferma importazione'}
              </Button>
            </DialogFooter>
          </div>
        )}

        {phase === 'done' && result && (
          <div className="space-y-3">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-sm text-emerald-900">
              ✅ Creati: <strong>{result.created}</strong> · Aggiornati: <strong>{result.updated}</strong> · Saltati: <strong>{result.skipped}</strong>
            </div>
            <DialogFooter>
              <Button onClick={onDone} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="import-done">Chiudi</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
